import { Search, Loader2, CornerDownLeft } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/shared/utils/cn';
import { useGlobalSearchQuery } from '@/modules/search/hooks/use-global-search-query';
import { MIN_SEARCH_LENGTH } from '@/modules/search/api/search.query';
import type { SearchResult } from '@/modules/search/types';

const DEBOUNCE_MS = 250;

/**
 * Global command-palette-style search for the top bar. Debounces the input,
 * fans out to the properties/leads/QR list endpoints (see search.service), and
 * renders grouped results with full keyboard support (↑/↓/↵, Esc, ⌘K to focus).
 */
export function GlobalSearch() {
    const navigate = useNavigate();

    const [query, setQuery]     = useState('');
    const [debounced, setDebounced] = useState('');
    const [open, setOpen]       = useState(false);
    const [active, setActive]   = useState(0);

    const rootRef  = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    // Debounce the term the query actually sees — matches the tables' 250-300ms.
    useEffect(() => {
        const t = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
        return () => clearTimeout(t);
    }, [query]);

    const { data: groups = [], isFetching } = useGlobalSearchQuery({ q: debounced });

    // Flatten groups into a single ordered list for arrow-key navigation.
    const flat: SearchResult[] = useMemo(() => groups.flatMap((g) => g.results), [groups]);

    // Reset the highlight whenever the result set changes.
    useEffect(() => { setActive(0); }, [flat]);

    // ⌘K / Ctrl+K focuses the search from anywhere.
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                inputRef.current?.focus();
                setOpen(true);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    // Close on outside click.
    useEffect(() => {
        const onClick = (e: MouseEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, []);

    const go = (result: SearchResult) => {
        navigate(result.href);
        setOpen(false);
        setQuery('');
        setDebounced('');
        inputRef.current?.blur();
    };

    const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') {
            setOpen(false);
            inputRef.current?.blur();
            return;
        }
        if (!open || flat.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActive((i) => (i + 1) % flat.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActive((i) => (i - 1 + flat.length) % flat.length);
        } else if (e.key === 'Enter') {
            e.preventDefault();
            const hit = flat[active];
            if (hit) go(hit);
        }
    };

    const showPanel   = open && debounced.length >= MIN_SEARCH_LENGTH;
    const hasResults  = flat.length > 0;
    // Running index across groups so highlight state maps to the flat list.
    let cursor = -1;

    return (
        <div ref={rootRef} className="relative max-w-md flex-1">
            <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-300" />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={onInputKeyDown}
                    placeholder="Search anything — properties, leads, QR codes…"
                    className="h-9 w-full rounded-lg border border-transparent bg-ink-50 pl-9 pr-12 text-sm transition placeholder:text-ink-300 focus:border-brand-sky focus:bg-white focus:outline-none"
                />
                {isFetching && debounced.length >= MIN_SEARCH_LENGTH ? (
                    <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-ink-300" />
                ) : (
                    <kbd className="absolute right-2 top-1/2 -translate-y-1/2 rounded border border-ink-100 bg-white px-1.5 py-0.5 font-ui text-[10px] font-semibold text-ink-500">
                        ⌘K
                    </kbd>
                )}
            </div>

            {showPanel && (
                <div className="absolute left-0 right-0 top-full z-40 mt-2 overflow-hidden rounded-xl2 border border-ink-100 bg-white shadow-pop">
                    <div className="max-h-[26rem] overflow-y-auto py-1">
                        {!hasResults ? (
                            <div className="px-4 py-8 text-center text-sm text-ink-500">
                                {isFetching ? 'Searching…' : (
                                    <>No results for <span className="font-medium text-brand-navy">“{debounced}”</span></>
                                )}
                            </div>
                        ) : (
                            groups.map((group) => {
                                const Icon = group.icon;
                                return (
                                    <div key={group.entity} className="pb-1">
                                        <div className="px-3 pb-1 pt-2 font-ui text-[10px] font-bold uppercase tracking-wider text-ink-400">
                                            {group.label}
                                        </div>
                                        {group.results.map((result) => {
                                            cursor += 1;
                                            const isActive = cursor === active;
                                            const idx = cursor;
                                            return (
                                                <button
                                                    key={result.id}
                                                    type="button"
                                                    onMouseEnter={() => setActive(idx)}
                                                    onClick={() => go(result)}
                                                    className={cn(
                                                        'flex w-full items-center gap-3 px-3 py-2 text-left transition',
                                                        isActive ? 'bg-brand-sky-50' : 'hover:bg-ink-50',
                                                    )}
                                                >
                                                    <span className={cn(
                                                        'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                                                        isActive ? 'bg-brand-sky text-white' : 'bg-ink-50 text-ink-500',
                                                    )}>
                                                        <Icon className="h-4 w-4" />
                                                    </span>
                                                    <span className="min-w-0 flex-1">
                                                        <span className="block truncate text-sm font-medium text-brand-navy">
                                                            {result.title}
                                                        </span>
                                                        {result.subtitle && (
                                                            <span className="block truncate text-xs text-ink-500">
                                                                {result.subtitle}
                                                            </span>
                                                        )}
                                                    </span>
                                                    {isActive && (
                                                        <CornerDownLeft className="h-3.5 w-3.5 shrink-0 text-brand-sky-500" />
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
